const initializedGalleries = new WeakMap();

class SupscepterProductGallery {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-sup-gallery-track]');
    this.panels = [...root.querySelectorAll('[data-sup-gallery-panel]')];
    this.thumbnails = [...root.querySelectorAll('[data-sup-gallery-thumbnail]')];
    this.previousButton = root.querySelector('[data-sup-gallery-previous]');
    this.nextButton = root.querySelector('[data-sup-gallery-next]');
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.activeIndex = 0;
    this.observer = null;
    this.abortController = new AbortController();
    this.onModeChange = this.updateMode.bind(this);
    this.bind();
    this.updateMode();
  }

  bind() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        this.activate(Number(thumbnail.dataset.mediaIndex), true);
      }, { signal: this.abortController.signal });
    }, { signal: this.abortController.signal });

    this.previousButton?.addEventListener('click', () => {
      const nextIndex = (this.activeIndex - 1 + this.panels.length) % this.panels.length;
      this.activate(nextIndex, false);
    });

    this.nextButton?.addEventListener('click', () => {
      const nextIndex = (this.activeIndex + 1) % this.panels.length;
      this.activate(nextIndex, false);
    }, { signal: this.abortController.signal });

    this.mobileQuery.addEventListener('change', this.onModeChange, { signal: this.abortController.signal });
  }

  updateMode() {
    this.observer?.disconnect();
    this.observer = null;

    if (this.mobileQuery.matches) {
      this.panels.forEach((panel) => panel.removeAttribute('aria-hidden'));
      if ('IntersectionObserver' in window && this.track) {
        this.observer = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (visible) this.updateSelection(Number(visible.target.dataset.mediaIndex));
          },
          { root: this.track, threshold: [0.55, 0.75] }
        );
        this.panels.forEach((panel) => this.observer.observe(panel));
      }
      return;
    }

    this.activate(this.activeIndex, false);
  }

  updateSelection(index) {
    this.activeIndex = index;
    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const selected = thumbnailIndex === index;
      thumbnail.classList.toggle('is-active', selected);
      if (selected) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });
  }

  activate(index, scrollOnMobile) {
    if (!this.panels[index]) return;
    this.updateSelection(index);

    if (this.mobileQuery.matches) {
      this.panels.forEach((panel) => panel.removeAttribute('aria-hidden'));
      if (scrollOnMobile) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.panels[index].scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
      }
      return;
    }

    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
    });
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = null;
    this.abortController.abort();
  }
}

function initializeSupscepterProductPage(scope = document) {
  scope.querySelectorAll('[data-sup-product-gallery]').forEach((gallery) => {
    if (initializedGalleries.has(gallery) || gallery.dataset.initialized === 'supscepter-product') return;
    gallery.dataset.initialized = 'supscepter-product';
    initializedGalleries.set(gallery, new SupscepterProductGallery(gallery));
  });
}

initializeSupscepterProductPage();

document.addEventListener('shopify:section:load', (event) => {
  initializeSupscepterProductPage(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-sup-product-gallery]').forEach((gallery) => {
    initializedGalleries.get(gallery)?.destroy();
    initializedGalleries.delete(gallery);
    delete gallery.dataset.initialized;
  });
});

